"use client";

import Image from "../image";
import {
  motion,
  useMotionValue,
  useMotionValueEvent,
  useSpring,
  useTransform,
  type MotionValue,
} from "motion/react";
import {
  createContext,
  useCallback,
  useContext,
  useRef,
  type KeyboardEvent,
  type ReactNode,
} from "react";

type SpringOptions = {
  bounce?: number;
  damping?: number;
  mass?: number;
  stiffness?: number;
};

type ImageComparisonContextValue = {
  position: MotionValue<number>;
  targetPosition: MotionValue<number>;
  setPositionFromPoint: (clientX: number) => void;
  setPosition: (value: number) => void;
};

const ImageComparisonContext =
  createContext<ImageComparisonContextValue | null>(null);

function useImageComparison() {
  const context = useContext(ImageComparisonContext);
  if (!context)
    throw new Error(
      "ImageComparison components must be nested inside ImageComparison.",
    );
  return context;
}

function clamp(value: number) {
  return Math.min(100, Math.max(0, value));
}

function joinClassNames(...classNames: Array<string | undefined>) {
  return classNames.filter(Boolean).join(" ");
}

export function ImageComparison({
  children,
  className,
  enableHover = false,
  springOptions,
}: {
  children: ReactNode;
  className?: string;
  enableHover?: boolean;
  springOptions?: SpringOptions;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const boundsRef = useRef<DOMRect | null>(null);
  const targetPosition = useMotionValue(50);
  const position = useSpring(
    targetPosition,
    springOptions ?? { stiffness: 900, damping: 80, mass: 0.18 },
  );

  // 缓存容器尺寸，避免每个指针事件都触发布局计算。
  const refreshBounds = useCallback(() => {
    boundsRef.current = containerRef.current?.getBoundingClientRect() ?? null;
  }, []);

  const setPosition = useCallback(
    (value: number) => {
      const nextValue = clamp(value);
      targetPosition.set(nextValue);
    },
    [targetPosition],
  );

  const setPositionFromPoint = useCallback(
    (clientX: number) => {
      const bounds = boundsRef.current;
      if (!bounds || bounds.width === 0) return;
      const nextValue = clamp(((clientX - bounds.left) / bounds.width) * 100);
      targetPosition.set(nextValue);
      position.jump(nextValue);
    },
    [position, targetPosition],
  );

  return (
    <ImageComparisonContext.Provider
      value={{ position, targetPosition, setPositionFromPoint, setPosition }}
    >
      <div
        ref={containerRef}
        className={joinClassNames("image-comparison", className)}
        onPointerEnter={refreshBounds}
        onPointerDown={refreshBounds}
        onPointerMove={(event) => {
          if (enableHover && event.pointerType === "mouse")
            setPositionFromPoint(event.clientX);
        }}
      >
        {children}
      </div>
    </ImageComparisonContext.Provider>
  );
}

export function ImageComparisonImage({
  alt,
  children,
  className,
  position: imagePosition,
  sizes = "(max-width: 900px) 100vw, 50vw",
  src,
}: {
  alt: string;
  className?: string;
  position: "left" | "right";
  sizes?: string;
  src: string;
  children?: ReactNode;
}) {
  const { position } = useImageComparison();
  const clipPath = useTransform(position, (value) =>
    imagePosition === "left"
      ? `inset(0 ${100 - value}% 0 0)`
      : `inset(0 0 0 ${value}%)`,
  );

  return (
    <motion.div
      className={joinClassNames("image-comparison-image", className)}
      style={{ clipPath }}
    >
      <Image src={src} alt={alt} fill sizes={sizes} />
      {children}
    </motion.div>
  );
}

export function ImageComparisonSlider({ className }: { className?: string }) {
  const { position, targetPosition, setPosition, setPositionFromPoint } =
    useImageComparison();
  const sliderRef = useRef<HTMLDivElement>(null);

  useMotionValueEvent(targetPosition, "change", (value) => {
    sliderRef.current?.setAttribute("aria-valuenow", String(Math.round(value)));
  });

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 10 : 5;
    if (event.key === "ArrowLeft" || event.key === "ArrowDown") {
      event.preventDefault();
      setPosition(targetPosition.get() - step);
    }
    if (event.key === "ArrowRight" || event.key === "ArrowUp") {
      event.preventDefault();
      setPosition(targetPosition.get() + step);
    }
    if (event.key === "Home") {
      event.preventDefault();
      setPosition(0);
    }
    if (event.key === "End") {
      event.preventDefault();
      setPosition(100);
    }
  };

  return (
    <motion.div
      ref={sliderRef}
      aria-label="调整两条增长路径的图片比例"
      aria-valuemax={100}
      aria-valuemin={0}
      aria-valuenow={Math.round(targetPosition.get())}
      className={joinClassNames("image-comparison-slider", className)}
      onKeyDown={handleKeyDown}
      onPointerDown={(event) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
      }}
      onPointerMove={(event) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.stopPropagation();
          setPositionFromPoint(event.clientX);
        }
      }}
      onPointerUp={(event) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId))
          event.currentTarget.releasePointerCapture(event.pointerId);
      }}
      role="slider"
      style={{ left: useTransform(position, (value) => `${value}%`) }}
      tabIndex={0}
    />
  );
}
